import type { FundingApplicationSchemas } from "@wayflyer/api-types";
import dayjs from "dayjs";
import type { Dayjs } from "dayjs";
import utc from "dayjs/plugin/utc";
import { MAX_DATE_NO_CSV } from "shared-ui";

import type { CoverageGapRange } from "shared-ui";

import { getHalfFill, mergeIntervals, totalDuration } from "../../../pages/banking/banking-accounts/coverage-intervals";
import type { Interval } from "../../../pages/banking/banking-accounts/coverage-intervals";
import { getReportableGaps } from "../../../pages/banking/banking-accounts/utils";
import type { DateRange, MonthCoverage, MonthCoverageFill } from "../../../pages/banking/banking-accounts/utils";

dayjs.extend(utc);

const SHORT_WINDOW_MONTHS = 6;
const LONG_WINDOW_MONTHS = 12;

export type StatementCoverageRange = { start: string; end: string };

export type AirStatementCoverage = {
  months: MonthCoverage[];
  isComplete: boolean;
};

const intersectIntervals = (a: Interval[], b: Interval[]): Interval[] =>
  a.flatMap((left) =>
    b.flatMap((right) => {
      const start = Math.max(left.start, right.start);
      const end = Math.min(left.end, right.end);
      return start <= end ? [{ start, end }] : [];
    })
  );

const airCalendarDay = (value: string): Dayjs => dayjs(dayjs.utc(value).format("YYYY-MM-DD"));

const getRequiredEnd = (period: FundingApplicationSchemas.DateTimeRangeSchema, now: Dayjs): Dayjs => {
  const messageEnd = period.end ? dayjs(period.end) : now;
  const isCurrentMonth = !period.end || messageEnd.format("YYYY-MM") === dayjs.utc(now).format("YYYY-MM");
  if (isCurrentMonth && messageEnd.date() <= MAX_DATE_NO_CSV) {
    return dayjs(messageEnd.subtract(1, "month").endOf("month").format("YYYY-MM-DD"));
  }
  return period.end ? airCalendarDay(period.end) : airCalendarDay(now.toISOString()).subtract(1, "day");
};

const requiredIntervals = (periods: FundingApplicationSchemas.DateTimeRangeSchema[], now: Dayjs): Interval[] =>
  mergeIntervals(
    periods
      .map((period) => ({
        start: airCalendarDay(period.start).startOf("day").valueOf(),
        end: getRequiredEnd(period, now).endOf("day").valueOf(),
      }))
      .filter((interval) => interval.start <= interval.end)
  );

const statementIntervals = (ranges: StatementCoverageRange[]): Interval[] =>
  mergeIntervals(
    ranges
      .map((range) => ({
        start: dayjs(range.start).startOf("day").valueOf(),
        end: dayjs(range.end).endOf("day").valueOf(),
      }))
      .filter((interval) => interval.start <= interval.end)
  );

const toDateRanges = (ranges: StatementCoverageRange[]): DateRange[] =>
  ranges.map((range) => ({ start: dayjs(range.start), end: dayjs(range.end) }));

const getReportableAirGaps = (required: Interval[], statementRanges: StatementCoverageRange[]): Interval[] => {
  const dateRanges = toDateRanges(statementRanges);
  return required.flatMap((interval) => getReportableGaps(dateRanges, interval.start, interval.end));
};

export const getAirRemainingRanges = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  statementRanges: StatementCoverageRange[],
  now: Dayjs = dayjs()
): CoverageGapRange[] => {
  const openEnds = periods
    .filter((period) => !period.end)
    .map((period) => getRequiredEnd(period, now).endOf("day").valueOf());
  return getReportableAirGaps(requiredIntervals(periods, now), statementRanges).map(({ start, end }) => ({
    start: dayjs(start).startOf("day"),
    end: dayjs(end).startOf("day"),
    openEnded: openEnds.includes(end),
  }));
};

export const getAirWindowStart = (periods: FundingApplicationSchemas.DateTimeRangeSchema[]): Dayjs | undefined =>
  periods
    .map((period) => airCalendarDay(period.start))
    .reduce<Dayjs | undefined>((earliest, day) => (earliest && !day.isBefore(earliest) ? earliest : day), undefined);

export const overlapsAirPeriods = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  statementRanges: StatementCoverageRange[],
  now: Dayjs = dayjs()
): boolean => intersectIntervals(requiredIntervals(periods, now), statementIntervals(statementRanges)).length > 0;

const getMonthFill = (required: Interval[], covered: Interval[], month: Interval): MonthCoverageFill => {
  const requiredDuration = totalDuration(required);
  const coveredDuration = totalDuration(covered);

  if (coveredDuration >= requiredDuration) {
    return "full";
  }
  if (coveredDuration === 0) {
    return "none";
  }

  return getHalfFill(covered, month);
};

const getWindowMonths = (required: Interval[], now: Dayjs): { firstMonth: Dayjs; lastMonth: Dayjs } => {
  const earliestRequiredStart = required[0].start;
  const lastRequiredMonth = dayjs(required[required.length - 1].end).startOf("month");
  const shortWindowStart = now.subtract(SHORT_WINDOW_MONTHS, "month").startOf("month");
  const longWindowStart = now.subtract(LONG_WINDOW_MONTHS, "month").startOf("month");

  if (earliestRequiredStart < longWindowStart.valueOf()) {
    return { firstMonth: dayjs(earliestRequiredStart).startOf("month"), lastMonth: lastRequiredMonth };
  }

  const lastDueMonth = getRequiredEnd({ start: now.toISOString(), end: null }, now).startOf("month");
  return {
    firstMonth: earliestRequiredStart >= shortWindowStart.valueOf() ? shortWindowStart : longWindowStart,
    lastMonth: lastDueMonth.isAfter(lastRequiredMonth) ? lastDueMonth : lastRequiredMonth,
  };
};

export const getAirRequiredMonths = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  now: Dayjs = dayjs()
): number => {
  const required = requiredIntervals(periods, now);
  if (required.length === 0) {
    return SHORT_WINDOW_MONTHS;
  }
  const shortWindowStart = now.subtract(SHORT_WINDOW_MONTHS, "month").startOf("month");
  return required[0].start >= shortWindowStart.valueOf() ? SHORT_WINDOW_MONTHS : LONG_WINDOW_MONTHS;
};

export const getAirStatementCoverage = (
  periods: FundingApplicationSchemas.DateTimeRangeSchema[],
  statementRanges: StatementCoverageRange[],
  now: Dayjs = dayjs(),
  locale = "en"
): AirStatementCoverage => {
  const required = requiredIntervals(periods, now);
  if (required.length === 0) {
    return { months: [], isComplete: true };
  }

  const covered = statementIntervals(statementRanges);
  const reportableGaps = getReportableAirGaps(required, statementRanges);
  const shortMonthFormatter = new Intl.DateTimeFormat(locale, { month: "short" });
  const fullMonthFormatter = new Intl.DateTimeFormat(locale, { month: "long" });

  const { firstMonth, lastMonth } = getWindowMonths(required, now);
  const monthCount = lastMonth.diff(firstMonth, "month") + 1;

  const months = Array.from({ length: monthCount }, (_, index): MonthCoverage => {
    const monthStart = firstMonth.add(index, "month");
    const monthInterval = { start: monthStart.valueOf(), end: monthStart.endOf("month").valueOf() };
    const requiredInMonth = intersectIntervals(required, [monthInterval]);
    const hasReportableGap = intersectIntervals(reportableGaps, [monthInterval]).length > 0;
    const monthDate = monthStart.toDate();

    return {
      label: shortMonthFormatter.format(monthDate),
      fullLabel: fullMonthFormatter.format(monthDate),
      fill:
        requiredInMonth.length === 0 || !hasReportableGap
          ? "full"
          : getMonthFill(requiredInMonth, intersectIntervals(requiredInMonth, covered), monthInterval),
    };
  });

  return { months, isComplete: reportableGaps.length === 0 };
};
